import { useCallback, useEffect, useRef, useState } from "react";
import {
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $insertNodes,
  $isRangeSelection,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  type ElementFormatType,
  type LexicalEditor,
  type TextFormatType,
} from "lexical";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import {
  $createHeadingNode,
  $isHeadingNode,
  HeadingNode,
  type HeadingTagType,
} from "@lexical/rich-text";
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListItemNode,
  ListNode,
} from "@lexical/list";
import { $isLinkNode, LinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { $setBlocksType } from "@lexical/selection";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { mergeRegister } from "@lexical/utils";
import {
  Bold,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  TextAlignCenter,
  TextAlignEnd,
  TextAlignJustify,
  TextAlignStart,
  Underline,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  extractEmailContentHtml,
  wrapEmailHtml,
} from "@/lib/defaultEmailCampaignHtml";
import { cn } from "@/lib/utils";

type BlockType = "paragraph" | HeadingTagType;

const BLOCK_TYPE_OPTIONS: { value: BlockType; label: string }[] = [
  { value: "paragraph", label: "Normal Text" },
  { value: "h1", label: "Heading 1" },
  { value: "h2", label: "Heading 2" },
  { value: "h3", label: "Heading 3" },
  { value: "h4", label: "Heading 4" },
  { value: "h5", label: "Heading 5" },
  { value: "h6", label: "Heading 6" },
];

const ALIGN_OPTIONS: {
  value: ElementFormatType;
  label: string;
  icon: typeof TextAlignStart;
}[] = [
  { value: "left", label: "Align left", icon: TextAlignStart },
  { value: "center", label: "Align center", icon: TextAlignCenter },
  { value: "right", label: "Align right", icon: TextAlignEnd },
  { value: "justify", label: "Justify", icon: TextAlignJustify },
];

const theme = {
  paragraph: "email-editor-paragraph",
  heading: {
    h1: "email-editor-h1",
    h2: "email-editor-h2",
    h3: "email-editor-h3",
    h4: "email-editor-h4",
    h5: "email-editor-h5",
    h6: "email-editor-h6",
  },
  list: {
    ul: "email-editor-list-ul",
    ol: "email-editor-list-ol",
    listitem: "email-editor-list-item",
  },
  link: "email-editor-link",
  text: {
    bold: "email-editor-text-bold",
    italic: "email-editor-text-italic",
    underline: "email-editor-text-underline",
  },
};

function onError(error: Error) {
  console.error(error);
}

function importHtmlIntoEditor({
  editor,
  html,
}: {
  editor: LexicalEditor;
  html: string;
}) {
  editor.update(() => {
    const root = $getRoot();
    root.clear();
    const content = extractEmailContentHtml(html);
    const parser = new DOMParser();
    const dom = parser.parseFromString(content, "text/html");
    const nodes = $generateNodesFromDOM(editor, dom);
    if (nodes.length > 0) {
      root.select();
      $insertNodes(nodes);
    }
  });
}

function SyncHtmlPlugin({
  html,
  onChange,
  syncExternalHtml,
}: {
  html: string;
  onChange: (html: string) => void;
  syncExternalHtml: boolean;
}) {
  const [editor] = useLexicalComposerContext();
  const skipNextExportRef = useRef(false);
  const lastHtmlRef = useRef(html);
  const didInitRef = useRef(false);

  useEffect(() => {
    if (!didInitRef.current) {
      didInitRef.current = true;
      skipNextExportRef.current = true;
      lastHtmlRef.current = html;
      importHtmlIntoEditor({ editor, html });
      return;
    }

    if (!syncExternalHtml) return;
    if (html === lastHtmlRef.current) return;
    lastHtmlRef.current = html;
    skipNextExportRef.current = true;
    importHtmlIntoEditor({ editor, html });
  }, [editor, html, syncExternalHtml]);

  const handleChange = useCallback(() => {
    if (skipNextExportRef.current) {
      skipNextExportRef.current = false;
      return;
    }

    editor.read(() => {
      const contentHtml = $generateHtmlFromNodes(editor, null);
      const fullHtml = wrapEmailHtml({ contentHtml });
      if (fullHtml === lastHtmlRef.current) return;
      lastHtmlRef.current = fullHtml;
      onChange(fullHtml);
    });
  }, [editor, onChange]);

  return (
    <OnChangePlugin ignoreSelectionChange onChange={handleChange} />
  );
}

function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [formats, setFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    link: false,
  });
  const [blockType, setBlockType] = useState<BlockType>("paragraph");
  const [elementFormat, setElementFormat] =
    useState<ElementFormatType>("left");

  useEffect(() => {
    const updateToolbar = () => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) {
        setFormats({ bold: false, italic: false, underline: false, link: false });
        setBlockType("paragraph");
        setElementFormat("left");
        return;
      }

      const node = selection.anchor.getNode();
      const parent = node.getParent();
      const topLevel = node.getTopLevelElementOrThrow();

      setFormats({
        bold: selection.hasFormat("bold"),
        italic: selection.hasFormat("italic"),
        underline: selection.hasFormat("underline"),
        link: $isLinkNode(parent) || $isLinkNode(node),
      });

      if ($isHeadingNode(topLevel)) {
        setBlockType(topLevel.getTag());
      } else {
        setBlockType("paragraph");
      }

      const format = topLevel.getFormatType();
      setElementFormat(format === "" || format === "start" ? "left" : format);
    };

    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(updateToolbar);
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          return false;
        },
        1
      )
    );
  }, [editor]);

  const formatText = (format: TextFormatType) => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
  };

  const formatElement = (format: ElementFormatType) => {
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, format);
  };

  const formatBlockType = (type: BlockType) => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      if (type === "paragraph") {
        $setBlocksType(selection, () => $createParagraphNode());
        return;
      }

      $setBlocksType(selection, () => $createHeadingNode(type));
    });
  };

  const toggleLink = () => {
    if (formats.link) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      return;
    }
    const url = window.prompt("Enter URL", "https://");
    if (!url) return;
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
  };

  return (
    <div className="flex flex-wrap items-center gap-1 border-b px-3 py-2 shrink-0 bg-background">
      <Select
        value={blockType}
        onValueChange={(value) => formatBlockType(value as BlockType)}
      >
        <SelectTrigger size="sm" className="w-[140px]" aria-label="Text style">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {BLOCK_TYPE_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="mx-1 h-5 w-px bg-border" />
      <Button
        type="button"
        variant={formats.bold ? "secondary" : "ghost"}
        size="icon-sm"
        onClick={() => formatText("bold")}
        aria-label="Bold"
      >
        <Bold />
      </Button>
      <Button
        type="button"
        variant={formats.italic ? "secondary" : "ghost"}
        size="icon-sm"
        onClick={() => formatText("italic")}
        aria-label="Italic"
      >
        <Italic />
      </Button>
      <Button
        type="button"
        variant={formats.underline ? "secondary" : "ghost"}
        size="icon-sm"
        onClick={() => formatText("underline")}
        aria-label="Underline"
      >
        <Underline />
      </Button>
      <div className="mx-1 h-5 w-px bg-border" />
      {ALIGN_OPTIONS.map(({ value, label, icon: Icon }) => (
        <Button
          key={value}
          type="button"
          variant={elementFormat === value ? "secondary" : "ghost"}
          size="icon-sm"
          onClick={() => formatElement(value)}
          aria-label={label}
        >
          <Icon />
        </Button>
      ))}
      <div className="mx-1 h-5 w-px bg-border" />
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={() =>
          editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
        }
        aria-label="Bullet list"
      >
        <List />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={() =>
          editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
        }
        aria-label="Numbered list"
      >
        <ListOrdered />
      </Button>
      <div className="mx-1 h-5 w-px bg-border" />
      <Button
        type="button"
        variant={formats.link ? "secondary" : "ghost"}
        size="icon-sm"
        onClick={toggleLink}
        aria-label="Link"
      >
        <LinkIcon />
      </Button>
    </div>
  );
}

export function EmailLexicalEditor({
  html,
  onChange,
  syncExternalHtml = true,
  className,
}: {
  html: string;
  onChange: (html: string) => void;
  syncExternalHtml?: boolean;
  className?: string;
}) {
  const initialConfig = {
    namespace: "EmailCampaignEditor",
    theme,
    onError,
    nodes: [HeadingNode, ListNode, ListItemNode, LinkNode],
  };

  return (
    <div className={cn("flex h-full min-h-0 flex-col", className)}>
      <style>{`
        .email-editor-paragraph { margin: 0 0 1em; }
        .email-editor-h1 { font-size: 1.75em; font-weight: 700; margin: 0 0 0.5em; line-height: 1.25; }
        .email-editor-h2 { font-size: 1.4em; font-weight: 700; margin: 0 0 0.5em; line-height: 1.3; }
        .email-editor-h3 { font-size: 1.15em; font-weight: 700; margin: 0 0 0.5em; line-height: 1.35; }
        .email-editor-h4 { font-size: 1.05em; font-weight: 700; margin: 0 0 0.5em; line-height: 1.4; }
        .email-editor-h5 { font-size: 1em; font-weight: 700; margin: 0 0 0.5em; line-height: 1.4; }
        .email-editor-h6 { font-size: 0.9em; font-weight: 700; margin: 0 0 0.5em; line-height: 1.4; }
        .email-editor-list-ul { list-style: disc; padding-left: 1.5em; margin: 0 0 1em; }
        .email-editor-list-ol { list-style: decimal; padding-left: 1.5em; margin: 0 0 1em; }
        .email-editor-list-item { margin: 0.25em 0; }
        .email-editor-link { color: #0563c1; text-decoration: underline; }
        .email-editor-text-bold { font-weight: 700; }
        .email-editor-text-italic { font-style: italic; }
        .email-editor-text-underline { text-decoration: underline; }
      `}</style>
      <LexicalComposer initialConfig={initialConfig}>
        <ToolbarPlugin />
        <div className="flex-1 min-h-0 overflow-auto px-4 py-6">
          <div className="relative mx-auto w-full max-w-[640px]">
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  className="relative min-h-[320px] outline-none text-base leading-relaxed text-[#333] font-[Arial,Helvetica,sans-serif]"
                  aria-placeholder="Write your email..."
                  placeholder={
                    <div className="pointer-events-none absolute top-0 left-0 text-muted-foreground">
                      Write your email...
                    </div>
                  }
                />
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
          </div>
        </div>
        <HistoryPlugin />
        <ListPlugin />
        <LinkPlugin />
        <SyncHtmlPlugin
          html={html}
          onChange={onChange}
          syncExternalHtml={syncExternalHtml}
        />
      </LexicalComposer>
    </div>
  );
}
